/**
 * Authentication Hook
 * Custom hook for authentication operations with 2FA support
 */

import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/auth.store';
import * as authService from '@/services/auth.service';
import { tokenManager } from '@/services/api.client';
import { socketClient } from '@/services/socket.client';
import { tokenRefreshService, TOKEN_EVENTS } from '@/services/token-refresh.service';
import type { LoginRequest, RegisterRequest } from '@/types/api.types';

export const useAuth = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    user,
    isAuthenticated,
    isLoading,
    error,
    login: setLogin,
    logout: setLogout,
    setLoading,
    setError,
    clearError,
  } = useAuthStore();

  // Login mutation — handles both direct login and 2FA redirect
  const loginMutation = useMutation({
    mutationFn: (credentials: LoginRequest) => authService.login(credentials),
    onSuccess: (result) => {
      if (authService.isTwoFactorRequired(result)) {
        // 2FA required — redirect to verification page
        navigate('/verify-2fa', {
          state: {
            twoFactorToken: result.twoFactorToken,
            email: result.email,
          },
          replace: true,
        });
        return;
      }

      // Direct login (trusted device) — proceed normally
      setLogin(result.user);
      queryClient.invalidateQueries({ queryKey: ['user'] });
      socketClient.connect();
      navigate('/');
    },
    onError: (error: Error) => {
      setError(error.message);
    },
  });

  // 2FA verification mutation
  const verifyTwoFactorMutation = useMutation({
    mutationFn: ({ twoFactorToken, code }: { twoFactorToken: string; code: string }) =>
      authService.verifyTwoFactor(twoFactorToken, code),
    onSuccess: (data) => {
      setLogin(data.user);
      queryClient.invalidateQueries({ queryKey: ['user'] });
      socketClient.connect();
      navigate('/');
    },
    onError: (error: Error) => {
      setError(error.message);
    },
  });

  // Register mutation
  const registerMutation = useMutation({
    mutationFn: (userData: RegisterRequest) => authService.register(userData),
    onSuccess: (data) => {
      setLogin(data.user);
      queryClient.invalidateQueries({ queryKey: ['user'] });
      socketClient.connect();

      if (data.user.pending_approval) {
        navigate('/pending-approval');
      } else {
        navigate('/');
      }
    },
    onError: (error: Error) => {
      setError(error.message);
    },
  });

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: () => authService.logout(),
    onSuccess: () => {
      setLogout();
      queryClient.clear();
      socketClient.disconnect();
      navigate('/login');
    },
    onError: (error: Error) => {
      console.error('Logout error:', error);
      setLogout();
      queryClient.clear();
      socketClient.disconnect();
      navigate('/login');
    },
  });

  // Get current user query
  useQuery({
    queryKey: ['user'],
    queryFn: () => authService.getCurrentUser(),
    enabled: isAuthenticated && tokenManager.hasTokens(),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  // Setup automatic token refresh
  useEffect(() => {
    if (!isAuthenticated || !tokenManager.hasTokens()) {
      return;
    }

    tokenRefreshService.start();

    const handleTokenExpired = () => {
      tokenManager.clearTokens();
      setLogout();
      queryClient.clear();
      socketClient.disconnect();
      navigate('/login');
    };

    const handleTokenRefreshed = () => {
      // WebSocket handles reconnection via its own listener
    };

    window.addEventListener(TOKEN_EVENTS.EXPIRED, handleTokenExpired);
    window.addEventListener(TOKEN_EVENTS.REFRESHED, handleTokenRefreshed);

    return () => {
      tokenRefreshService.stop();
      window.removeEventListener(TOKEN_EVENTS.EXPIRED, handleTokenExpired);
      window.removeEventListener(TOKEN_EVENTS.REFRESHED, handleTokenRefreshed);
    };
  }, [isAuthenticated, navigate, queryClient, setLogout]);

  const login = async (credentials: LoginRequest) => {
    clearError();
    setLoading(true);
    try {
      await loginMutation.mutateAsync(credentials);
    } finally {
      setLoading(false);
    }
  };

  const verifyTwoFactor = async (twoFactorToken: string, code: string) => {
    clearError();
    setLoading(true);
    try {
      await verifyTwoFactorMutation.mutateAsync({ twoFactorToken, code });
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData: RegisterRequest) => {
    clearError();
    setLoading(true);
    try {
      await registerMutation.mutateAsync(userData);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    clearError();
    setLoading(true);
    try {
      await logoutMutation.mutateAsync();
    } finally {
      setLoading(false);
    }
  };

  return {
    user,
    isAuthenticated,
    isLoading: isLoading || loginMutation.isPending || registerMutation.isPending || logoutMutation.isPending || verifyTwoFactorMutation.isPending,
    error,
    login,
    verifyTwoFactor,
    register,
    logout,
    clearError,
  };
};
